import { useState, useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  renderInlineMarkdown,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@org/ui';
import { useTeacherId } from '../../../components/teacher-picker';
import { useTranslation } from 'react-i18next';
import {
  AutoFlagReasons,
  CategoryPills,
  FlagPill,
  MetricsGrid,
  TeacherFlagSummary,
  metricsWeeks,
} from '../../../components/student-flag-badges';
import { StudentFlagDialog } from '../../../components/student-flag-dialog';
import { MellinAiPracticeCard } from '../../../components/mellinai-practice-card';
import {
  DotLegend,
  ExtraClassDots,
  SemesterDots,
  scorecardHref,
  type SessionHomeworkStatus,
} from '../../../components/semester-dots';

export const Route = createFileRoute('/students/$studentId/')({
  component: StudentProfilePage,
});

type Profile = RouterOutputs['teacher']['students']['profile'];
type ProfileSession = Profile['sessions'][number];

const HOMEWORK_STATUS_STYLE: Record<SessionHomeworkStatus, { pill: string; legend: string }> = {
  full: { pill: 'bg-emerald-100 text-emerald-700', legend: 'full' },
  partial: { pill: 'bg-amber-100 text-amber-800', legend: 'partial' },
  none: { pill: 'bg-rose-100 text-rose-700', legend: 'none' },
  'no-exercises': { pill: 'bg-gray-100 text-gray-600', legend: 'noExercises' },
  upcoming: { pill: 'bg-gray-100 text-gray-600', legend: 'upcoming' },
};

const formatDate = (value: Date | string | null | undefined, fallback: string) =>
  value ? new Date(value).toLocaleDateString() : fallback;

function StudentProfilePage() {
  const { t } = useTranslation('admin');
  const { studentId } = Route.useParams();
  const teacherId = useTeacherId();
  const { data, isLoading } = trpc.teacher.students.profile.useQuery({ teacherId, studentId });
  const studentList = trpc.teacher.students.list.useQuery({ teacherId });
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);

  const navigation = useMemo(() => {
    if (!studentList.data?.students) return null;
    const students = studentList.data.students;
    const currentIndex = students.findIndex((s) => s.studentId === studentId);
    if (currentIndex === -1) return null;
    return {
      prev: currentIndex > 0 ? students[currentIndex - 1] : null,
      next: currentIndex < students.length - 1 ? students[currentIndex + 1] : null,
      current: currentIndex + 1,
      total: students.length,
    };
  }, [studentList.data, studentId]);

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-5/6" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const {
    student,
    person,
    presenceHistory,
    sessions,
    communityEvents,
    semester,
    metrics,
    autoFlag,
    teacherFlag,
    flagHistory,
  } = data;
  const studentName =
    `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student.email || t('teacher.studentProfile.studentFallback');
  const weeks = metricsWeeks(metrics);
  const semesterLine = semester.hasGroupDates
    ? t('teacher.students.semesterSince', { date: new Date(metrics.since).toLocaleDateString() })
    : t('teacher.students.noGroupDates', { weeks });

  // Past sessions with a class attached, oldest first (the whole semester).
  const deliveredSessions = [...sessions]
    .filter(
      (session): session is ProfileSession & { homework: NonNullable<ProfileSession['homework']> } =>
        session.homework !== null && session.homework.status !== 'upcoming',
    )
    .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());

  return (
    <div className="space-y-6">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild className="px-0 text-sm text-[#cf2e2e]">
          <Link to="/students" search={{ teacherId: undefined }}>
            &#8592; {t('teacher.studentProfile.backToStudents')}
          </Link>
        </Button>
        {navigation && (
          <div className="flex items-center gap-2">
            {navigation.prev ? (
              <Button variant="outline" size="sm" asChild className="text-xs">
                <Link
                  to="/students/$studentId"
                  params={{ studentId: navigation.prev.studentId }}
                  search={{ teacherId: undefined }}
                >
                  &#8592; {navigation.prev.displayName?.split(' ')[0] ?? t('teacher.studentProfile.previous')}
                </Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled className="text-xs">
                &#8592; {t('teacher.studentProfile.previous')}
              </Button>
            )}
            <span className="text-xs text-gray-500">
              {navigation.current}/{navigation.total}
            </span>
            {navigation.next ? (
              <Button variant="outline" size="sm" asChild className="text-xs">
                <Link
                  to="/students/$studentId"
                  params={{ studentId: navigation.next.studentId }}
                  search={{ teacherId: undefined }}
                >
                  {navigation.next.displayName?.split(' ')[0] ?? t('teacher.studentProfile.next')} &#8594;
                </Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled className="text-xs">
                {t('teacher.studentProfile.next')} &#8594;
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.studentProfile.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">
            {studentName}
            {student.nickname ? (
              <span className="ml-2 text-xl font-normal text-gray-500">({student.nickname})</span>
            ) : null}
          </h1>
          <p className="text-sm text-gray-600">{student.email ?? t('common.fallbacks.noEmail')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild className="text-xs">
            <Link to="/students/$studentId/feedback" params={{ studentId }} search={{ teacherId: undefined }}>
              {t('teacher.studentProfile.feedbacksButton')}
            </Link>
          </Button>
          <Badge className="rounded-full bg-[#fff5f8] text-[#cf2e2e]">
            {student.groups.length ? student.groups.join(' • ') : t('teacher.studentProfile.individualStudent')}
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="performance">
        <TabsList className="bg-[#fff5f8]">
          <TabsTrigger value="performance" className="data-[state=active]:text-[#cf2e2e]">
            {t('teacher.studentProfile.tabs.performance')}
          </TabsTrigger>
          <TabsTrigger value="profile" className="data-[state=active]:text-[#cf2e2e]">
            {t('teacher.studentProfile.tabs.profile')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="mt-4 space-y-6">
          {/* 1. Alert */}
          <Card className="border border-rose-50 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl text-gray-900">{t('teacher.studentProfile.risk.title')}</CardTitle>
              <CardDescription>{t('teacher.studentProfile.risk.description')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{t('teacher.flags.auto')}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <FlagPill level={autoFlag.level} />
                  <span className="text-xs text-gray-400">{t('teacher.flags.autoHint', { weeks })}</span>
                </div>
                <CategoryPills autoFlag={autoFlag} />
                <AutoFlagReasons autoFlag={autoFlag} />
                <MetricsGrid metrics={metrics} />
              </div>
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{t('teacher.flags.current')}</p>
                {teacherFlag ? (
                  <TeacherFlagSummary flag={teacherFlag} showManualLabel={false} />
                ) : (
                  <EmptyHint text={t('teacher.flags.historyEmpty')} />
                )}
                <Button size="sm" className="text-xs" onClick={() => setFlagDialogOpen(true)}>
                  {t('teacher.students.flagButton')}
                </Button>
                {flagHistory.length > 1 && (
                  <div className="space-y-2 pt-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{t('teacher.flags.history')}</p>
                    <div className="max-h-[260px] space-y-2 overflow-y-auto">
                      {flagHistory.slice(1).map((entry) => (
                        <div key={entry.studentFlagId} className="rounded-xl bg-[#fff9fb] px-4 py-2">
                          <TeacherFlagSummary flag={entry} showManualLabel={false} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 2. Semester overview */}
          <Card className="border border-rose-50 bg-white shadow-sm">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-xl text-gray-900">{t('teacher.studentProfile.semester.title')}</CardTitle>
                  <CardDescription>{t('teacher.studentProfile.semester.description')}</CardDescription>
                </div>
                <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-3 py-1 text-[#cf2e2e]">
                  {t('teacher.students.sessionsLabel', { count: sessions.length })}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {sessions.length === 0 && communityEvents.length === 0 ? (
                <EmptyHint text={t('teacher.studentProfile.semester.noSessions')} />
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                        {t('teacher.studentProfile.semester.presence')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {metrics.presence.rate !== null
                          ? t('teacher.students.presenceRate', { rate: metrics.presence.rate })
                          : t('teacher.flags.metrics.noData')}
                      </p>
                    </div>
                    <SemesterDots sessions={sessions} kind="presence" studentId={studentId} />
                    <DotLegend kind="presence" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                        {t('teacher.studentProfile.semester.homework')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {metrics.homework.rate !== null
                          ? t('teacher.students.homeworkSemesterRate', { rate: metrics.homework.rate })
                          : t('teacher.flags.metrics.noData')}
                      </p>
                    </div>
                    <SemesterDots sessions={sessions} kind="homework" studentId={studentId} />
                    <DotLegend kind="homework" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                        {t('teacher.studentProfile.semester.extraClasses')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {metrics.community.rate !== null
                          ? t('teacher.students.communityRate', {
                              done: metrics.community.done,
                              total: metrics.community.total,
                            })
                          : t('teacher.flags.metrics.noData')}
                      </p>
                    </div>
                    <ExtraClassDots events={communityEvents} minSlots={5} />
                  </div>
                </>
              )}
              <p className="text-xs text-gray-400">{semesterLine}</p>
            </CardContent>
          </Card>

          {/* 3 + 4. Recent attendance and extra classes */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="border border-rose-50 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">{t('teacher.studentProfile.recentPresence.title')}</CardTitle>
                <CardDescription>{t('teacher.studentProfile.recentPresence.description')}</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[400px] space-y-2 overflow-y-auto">
                {presenceHistory.length === 0 && <EmptyHint text={t('teacher.studentProfile.recentPresence.empty')} />}
                {presenceHistory.map((entry, index) => (
                  <a
                    // The same event can appear twice (two scorecards), so the index is part of the key.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${entry.scorecardId}-${index}`}
                    href={scorecardHref(entry)}
                    target="_blank"
                    rel="noopener"
                    title={t('teacher.studentProfile.recentPresence.openRecord')}
                    className="flex items-center justify-between gap-3 rounded-xl bg-[#fff9fb] px-4 py-2 transition hover:bg-[#fff0f4]"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {entry.title ?? t('common.fallbacks.class')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(entry.eventDate, t('common.fallbacks.dateNotProvided'))}
                      </p>
                    </div>
                    <StatusDot
                      present={entry.isPresent}
                      label={
                        entry.isPresent
                          ? t('teacher.studentProfile.recentPresence.present')
                          : t('teacher.studentProfile.recentPresence.absent')
                      }
                    />
                  </a>
                ))}
              </CardContent>
            </Card>

            <Card className="border border-rose-50 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">{t('teacher.studentProfile.extraClasses.title')}</CardTitle>
                <CardDescription>{t('teacher.studentProfile.extraClasses.description')}</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[400px] space-y-2 overflow-y-auto">
                {communityEvents.length === 0 && <EmptyHint text={t('teacher.studentProfile.extraClasses.empty')} />}
                {communityEvents.map((entry) => (
                  <div
                    key={entry.eventId}
                    className="flex items-center justify-between gap-3 rounded-xl bg-[#fff9fb] px-4 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {entry.title ?? t('common.fallbacks.event')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(entry.eventDate, t('common.fallbacks.dateNotProvided'))}
                      </p>
                    </div>
                    <StatusDot
                      present={entry.attended}
                      label={
                        entry.attended
                          ? t('teacher.studentProfile.recentPresence.present')
                          : t('teacher.studentProfile.recentPresence.absent')
                      }
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* 5. Activities delivered */}
          <Card className="border border-rose-50 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">{t('teacher.studentProfile.homework.title')}</CardTitle>
              <CardDescription>{t('teacher.studentProfile.homework.description', { weeks })}</CardDescription>
            </CardHeader>
            <CardContent className="max-h-[480px] space-y-2 overflow-y-auto">
              {deliveredSessions.length === 0 && <EmptyHint text={t('teacher.studentProfile.homework.empty')} />}
              {deliveredSessions.map((session) => {
                const style = HOMEWORK_STATUS_STYLE[session.homework.status];
                return (
                  <div key={session.eventId} className="rounded-xl bg-[#fff9fb] px-4 py-2">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-800">
                          {session.className ?? session.title ?? t('common.fallbacks.class')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(session.eventDate, t('common.fallbacks.dateNotProvided'))}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${style.pill}`}>
                          {t(`teacher.students.legend.${style.legend}`)}
                        </span>
                        {session.classId && (
                          <Link
                            to="/classes/$classId/student/$studentId"
                            params={{ classId: session.classId, studentId }}
                            search={{ teacherId: undefined, block: undefined }}
                            className="text-xs text-[#cf2e2e] hover:underline"
                          >
                            {t('teacher.studentProfile.homework.openSession')}
                          </Link>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">
                      {t('teacher.studentProfile.homework.sections', {
                        done: session.homework.completedBlocks,
                        total: session.homework.totalBlocks,
                      })}
                      {' · '}
                      {session.homework.submittedExercises}/{session.homework.totalExercises}{' '}
                      {t('teacher.students.homework').toLowerCase()}
                    </p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
          {/* 6. Free practice with MellinAI (AI summaries) */}
          <MellinAiPracticeCard studentId={studentId} />
        </TabsContent>

        <TabsContent value="profile" className="mt-4 space-y-6">
          <Card className="border border-rose-50 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl text-gray-900">{t('teacher.studentProfile.personalInfo.title')}</CardTitle>
              <CardDescription>{t('teacher.studentProfile.personalInfo.description')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <InfoField label={t('teacher.studentProfile.personalInfo.nickname')} value={student.nickname} />
              <InfoField label={t('teacher.studentProfile.personalInfo.profession')} value={person?.profession} />
              <InfoField label={t('teacher.studentProfile.personalInfo.nationality')} value={person?.nationality} />
              <InfoField label={t('teacher.studentProfile.personalInfo.whatsapp')} value={student.phone} />
              <InfoField label={t('teacher.studentProfile.personalInfo.birthDate')} value={person?.dateOfBirth} />
              <InfoField label={t('teacher.studentProfile.personalInfo.educationLevel')} value={person?.educationLevel} />
            </CardContent>
          </Card>

          <Card className="border border-rose-50 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl text-gray-900">{t('teacher.studentProfile.leveling.title')}</CardTitle>
              <CardDescription>{t('teacher.studentProfile.leveling.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {person?.leveling
                  ? renderInlineMarkdown(person.leveling, 'leveling-notes')
                  : t('teacher.studentProfile.leveling.empty')}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <StudentFlagDialog
        open={flagDialogOpen}
        onOpenChange={setFlagDialogOpen}
        studentId={studentId}
        studentName={studentName}
        currentFlag={teacherFlag?.flag ?? null}
      />
    </div>
  );
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{label}</p>
      <p className="text-sm text-gray-800">{value ?? '—'}</p>
    </div>
  );
}

function StatusDot({ present, label }: { present: boolean; label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-gray-500">
      <span
        role="img"
        aria-label={label}
        title={label}
        className={`inline-block h-3 w-3 rounded-full ${present ? 'bg-emerald-500' : 'bg-rose-400'}`}
      />
      {label}
    </span>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="rounded-lg border border-dashed border-rose-200 px-4 py-3 text-sm text-gray-500">{text}</p>;
}
